import { firestore } from "firebase";

import {
  AppointmentInfo,
  DryingProjectOverview,
  getAllOpenAppointments,
} from "@/data/models/DryingProjectOverview";

import { ProjectStateFilterType } from "@/utils/ProjectStateFilter";
import { ProjectStateFilterCounter } from "@/utils/ProjectStateFilterCounter";
import { CockpitCardType } from "@/data/models/CockpitCardType";

import { UserLookup } from "@/data/models/UserLookup";
import { toStartOfDay } from "@/utils/DateHelper";

import {
  getAppointmentTypeDescription,
  getLastestProjectState,
} from "@/data/models/ProjectState";

import { DateTime } from "luxon";
import ProjectOverview, { isProjectOverviewWebsiteOnly } from "@/data/models/ProjectOverview";

import { formatJsDate } from "@/utils/DateFormatter";

import router from "@/router/router";
import User from "@/data/models/User";
import { ProjectSortOrder } from "@/data/models/ProjectSortOrder";
import LeakDetectionProjectOverview from "@/data/models/LeakDetectionProjectOverview";
import { NewProjectScope } from "@/data/repository/ProjectRepository";
import { BranchFilter } from "@/data/models/BranchFilter";
import { setString, STORAGE_KEY_LAST_VISITED_ITEM_LEVEL_1 } from "./SecureLocalStorage";

interface CreateCockpitInfoContext {
  userLookup: UserLookup;

  vue: Vue;
  targetUser: User;
  branchFilter: BranchFilter;

  addRelevantProjects: boolean;
  addAllProjects: boolean;
  addAppointmentItems: boolean;

  doesClientHaveApp: boolean;

  createProject: boolean;
}

export interface CockpitProjectsInfo {
  projectOrganisationItems: CockpitEntryInfo[];
  appointmentManagementItems: CockpitEntryInfo[];
  allProjectPhaseItems: CockpitEntryInfo[];
  relevantProjects: CockpitEntryInfo[];
  myAppointmentItems: CockpitEntryInfo[];
  myProjectItems: CockpitEntryInfo[];
  projectsNoApp: CockpitEntryInfo[];
}

export enum CockpitEntryInfoType {
  EventInfo,
  Counter,
  Appointment,
  Header,
  Text,
}

export interface MyAppointmentInfo extends AppointmentInfo {
  projectName: string;
  projectId: string;
  sortTimestamp: number;
  isTimeSet: boolean;
}

export interface ProjectCounterInfo {
  name: string;
  count: number | undefined | string;
}

export interface CockpitEntryInfo {
  id: number | string;
  infoType: CockpitEntryInfoType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  shouldBeDisplayed: boolean;
  shouldBeHighlighted?: boolean;
  sortValue?: number;
  onClickCallback?: () => void;
}

function mapFilterCountersToCockpitItems(
  filters: ProjectStateFilterCounter[],
  cockpitCardType: CockpitCardType,
  setTargetBranchId: boolean,
  context: CreateCockpitInfoContext,

  sortOrder?: ProjectSortOrder,
  ignoreUserRequirements?: boolean
): CockpitEntryInfo[] {
  const { branchFilter, vue } = context;

  return filters
    .map((filterCounter, index) => {
      return {
        id: index,
        infoType: CockpitEntryInfoType.Counter,
        data: {
          name: filterCounter.getDescription(vue),
          count: filterCounter.numMatchingProjects,
        },
        shouldBeDisplayed: filterCounter.numMatchingProjects > 0,
        onClickCallback: () => {
          router.push({
            path: "cockpit/projects",
            query: {
              cockpitCardType: cockpitCardType.toString(),
              projectStateFilterType: filterCounter.filter.filterType.toString(),
              ignoreUserRequirements: ignoreUserRequirements
                ? ignoreUserRequirements.toString()
                : null,
              targetBranchId: setTargetBranchId
                ? branchFilter.isSingleBranchFilter
                  ? branchFilter.branch?.id ?? null
                  : null
                : null,
              sortOrder: (
                sortOrder ?? ProjectSortOrder.NEWEST_FIRST
              ).toString(),
              isProjectsTab: "false"
            },
          });
        },
      };
    })
    .filter((value) => value.shouldBeDisplayed);
}

function matchesTargetUser(user: User, userIdentifier?: string) {
  if (!user || userIdentifier === "") {
    return false;
  }

  return (
    userIdentifier === user.id ||
    userIdentifier === user.staffNumber ||
    userIdentifier === user.nickname ||
    userIdentifier === `${user.firstName} ${user.lastName}`
  );
}

function mapMyAppointmentInfoToCockpitEntry(
  id: number,
  myAppointmentInfo: MyAppointmentInfo,
  vue: Vue
): CockpitEntryInfo {
  const appointmentDate = new Date(myAppointmentInfo.appointmentDate);
  const formattedDate = appointmentDate
    ? formatJsDate(appointmentDate, myAppointmentInfo.isTimeSet)
    : "";

  const appointmentType = myAppointmentInfo.appointmentType;
  const appointmentTypeDescription =
    appointmentType !== undefined
      ? getAppointmentTypeDescription(vue, appointmentType)
      : `${appointmentType}`;

  const projectName = myAppointmentInfo.projectName;
  const projectId = myAppointmentInfo.projectId;

  const appointmentDescription = `${formattedDate} ${appointmentTypeDescription}\n${projectName}`;

  return {
    id: id,
    infoType: CockpitEntryInfoType.Appointment,
    data: {
      appointmentDescription: appointmentDescription,
    },
    shouldBeDisplayed: true,
    onClickCallback: () => {
      router.push({ path: `cockpit/projects/${projectId}` });
    },
  };
}

function getMyAppointmentItems(
  projects: ProjectOverview[],
  context: CreateCockpitInfoContext
) {
  const { targetUser, vue } = context;

  const myAppointments: MyAppointmentInfo[] = [];

  projects
    .filter(
      (projectOverview) =>
        !isProjectOverviewWebsiteOnly(projectOverview)
    )
    .forEach((projectOverview) => {
      const allOpenAppointments = getAllOpenAppointments(projectOverview);

      allOpenAppointments
        .filter(
          (appointmentInfo) =>
            (
              matchesTargetUser(targetUser, appointmentInfo.responsibleUser) ||
              (appointmentInfo.additionalUsers ?? []).some((identifier) =>
                matchesTargetUser(targetUser, identifier)
              )
            ) &&
            appointmentInfo.appointmentDate
        )
        .forEach((appointmentInfo) => {
          const appointmentDate = DateTime.fromISO(
            appointmentInfo.appointmentDate
          );

          if (appointmentDate) {
            const isTimeSet =
              appointmentDate.minute !== 0 || appointmentDate.hour !== 0;
            const sortTimestamp = isTimeSet
              ? appointmentDate.toMillis()
              : appointmentDate.endOf("day").toMillis();

            myAppointments.push({
              ...appointmentInfo,
              projectName: projectOverview.projectName,
              sortTimestamp: sortTimestamp,
              isTimeSet: isTimeSet,
              projectId: projectOverview.id,
            });
          }
        });
    });

  const appointmentsOverdue: MyAppointmentInfo[] = [];
  const appointmentsToday: MyAppointmentInfo[] = [];
  const appointmentsTomorrow: MyAppointmentInfo[] = [];
  const appointmentsLater: MyAppointmentInfo[] = [];

  const now = new Date();
  const tommorow = new Date();
  tommorow.setDate(tommorow.getDate() + 1);

  const nowTimestamp = now.getTime();

  const endOfTodayTimestamp = DateTime.fromJSDate(now)
    .endOf("day")
    .toMillis();

  const endOfTommorowTimestamp = DateTime.fromJSDate(tommorow)
    .endOf("day")
    .toMillis();

  myAppointments
    .sort((a, b) => a.sortTimestamp - b.sortTimestamp)
    .forEach((myAppointment) => {
      const sortTimestamp = myAppointment.sortTimestamp;
      if (sortTimestamp < nowTimestamp) {
        appointmentsOverdue.push(myAppointment);
      } else if (sortTimestamp <= endOfTodayTimestamp) {
        appointmentsToday.push(myAppointment);
      } else if (sortTimestamp <= endOfTommorowTimestamp) {
        appointmentsTomorrow.push(myAppointment);
      } else {
        appointmentsLater.push(myAppointment);
      }
    });

  const cockpitInfoItems: CockpitEntryInfo[] = [];
  let id = 0;

  if (appointmentsOverdue.length) {
    cockpitInfoItems.push({
      id: id++,
      infoType: CockpitEntryInfoType.Header,
      data: {
        header: vue.$t("cockpit.appointments.overdue").toString(),
      },
      shouldBeDisplayed: true,
    });

    appointmentsOverdue.forEach((myAppointmentInfo) =>
      cockpitInfoItems.push(
        mapMyAppointmentInfoToCockpitEntry(id++, myAppointmentInfo, vue)
      )
    );
  }

  if (appointmentsToday.length) {
    cockpitInfoItems.push({
      id: id++,
      infoType: CockpitEntryInfoType.Header,
      data: {
        header: vue.$t("cockpit.appointments.today").toString(),
      },
      shouldBeDisplayed: true,
    });

    appointmentsToday.forEach((myAppointmentInfo) =>
      cockpitInfoItems.push(
        mapMyAppointmentInfoToCockpitEntry(id++, myAppointmentInfo, vue)
      )
    );
  }

  if (appointmentsTomorrow.length) {
    cockpitInfoItems.push({
      id: id++,
      infoType: CockpitEntryInfoType.Header,
      data: {
        header: vue.$t("cockpit.appointments.tomorrow").toString(),
      },
      shouldBeDisplayed: true,
    });

    appointmentsTomorrow.forEach((myAppointmentInfo) =>
      cockpitInfoItems.push(
        mapMyAppointmentInfoToCockpitEntry(id++, myAppointmentInfo, vue)
      )
    );
  }

  if (appointmentsLater.length) {
    cockpitInfoItems.push({
      id: id++,
      infoType: CockpitEntryInfoType.Header,
      data: {
        header: vue.$t("cockpit.appointments.later").toString(),
      },
      shouldBeDisplayed: true,
    });

    appointmentsLater.forEach((myAppointmentInfo) =>
      cockpitInfoItems.push(
        mapMyAppointmentInfoToCockpitEntry(id++, myAppointmentInfo, vue)
      )
    );
  }

  return cockpitInfoItems;
}

export function createCockpitProjectInfo(
  projectOverviews: {
    recentDryingProjects: DryingProjectOverview[];
    recentLeakDetectionProjects: LeakDetectionProjectOverview[];
  },
  context: CreateCockpitInfoContext
): CockpitProjectsInfo {
  const { userLookup, vue, targetUser, branchFilter } = context;

  const {
    recentDryingProjects,
    recentLeakDetectionProjects,
  } = projectOverviews;

  const recentProjects = [
    ...recentDryingProjects,
    ...recentLeakDetectionProjects,
  ];

  const today = toStartOfDay(firestore.Timestamp.now()).toDate();

  if (!context.doesClientHaveApp) {
    const projectsNoAppFilters = [
      new ProjectStateFilterCounter(ProjectStateFilterType.ALL_PROJECTS),
      new ProjectStateFilterCounter(ProjectStateFilterType.WITH_HY_DRY_DEVICES),
      new ProjectStateFilterCounter(
        ProjectStateFilterType.RECENTLY_FINISHED,
        []
      ),
      new ProjectStateFilterCounter(
        ProjectStateFilterType.MISSING_PROJECT_CONCLUSION_NO_APP,
        []
      ),
    ];

    recentDryingProjects.forEach((projectOverview) => {
      const step = getLastestProjectState(projectOverview, today);

      projectsNoAppFilters.forEach((filter) => {
        filter.visit(projectOverview, targetUser, userLookup, today, step);
      });
    });

    const projectsNoApp = context.createProject ? [
      ...mapFilterCountersToCockpitItems(
        projectsNoAppFilters,
        CockpitCardType.ALL_PROJECTS_BY_PHASES,
        true,
        context
      ),
      {
        id: -1,
        data: {
          name: vue.$t("cockpit.createNewProject").toString(),
          count: "+",
        },
        infoType: CockpitEntryInfoType.Counter,
        shouldBeDisplayed: context.createProject,
        onClickCallback: () => {
          setString(STORAGE_KEY_LAST_VISITED_ITEM_LEVEL_1);

          router.push({
            path: "cockpit/createProject",
            query: {
              newProjectScope: NewProjectScope.DRYING,
            },
          });
        },
      },
    ] :
    [
      ...mapFilterCountersToCockpitItems(
        projectsNoAppFilters,
        CockpitCardType.ALL_PROJECTS_BY_PHASES,
        true,
        context
      )
    ];

    return {
      allProjectPhaseItems: [],
      myAppointmentItems: [],
      projectOrganisationItems: [],
      appointmentManagementItems: [],
      myProjectItems: [],
      projectsNoApp: projectsNoApp,
      relevantProjects: [],
    };
  }

  const projectOrganisationFilters = [
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MISSING_PROJECT_APPROVAL
    ),
    new ProjectStateFilterCounter(ProjectStateFilterType.MISSING_FIRST_CONTACT),
    new ProjectStateFilterCounter(ProjectStateFilterType.ASSIGN_DEVICES),
    new ProjectStateFilterCounter(ProjectStateFilterType.INSURANCE_OFFER),
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MISSING_DRYING_APPROVAL_PROJECTORGANISATION
    ),
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MISSING_PROJECT_CONCLUSION_PROJECTORGANISATION
    ),
  ];

  const appointmentManagementFilters = [
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MAKE_LEAK_DETECTION_APPOINTMENT
    ),
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MAKE_DAMAGE_ASSESSMENT_APPOINTMENT
    ),
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MAKE_SETUP_DRYING_APPOINTMENT
    ),
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MAKE_INTERMEDIATE_MEASUREMENT_APPOINTMENT
    ),
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MAKE_DRYING_CONCLUSION_APPOINTMENT
    ),
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MAKE_REPAIR_APPOINTMENT
    ),
  ];

  const allProjectsByPhasesFilters = [
    new ProjectStateFilterCounter(ProjectStateFilterType.OVERALL, []),
    new ProjectStateFilterCounter(ProjectStateFilterType.NEW_PROJECT, []),
    new ProjectStateFilterCounter(ProjectStateFilterType.LEAK_DETECTION, []),
    new ProjectStateFilterCounter(ProjectStateFilterType.DAMAGE_ASSESSMENT, []),
    new ProjectStateFilterCounter(
      ProjectStateFilterType.MISSING_DRYING_APPROVAL,
      []
    ),
    new ProjectStateFilterCounter(ProjectStateFilterType.DRYING, []),
    new ProjectStateFilterCounter(ProjectStateFilterType.REPAIR, []),
    new ProjectStateFilterCounter(
      ProjectStateFilterType.OVERDUE,
      []
    ),
  ];

  const relevantProjectFilters = context.addRelevantProjects
    ? [
        new ProjectStateFilterCounter(ProjectStateFilterType.OVERALL),
        new ProjectStateFilterCounter(
          ProjectStateFilterType.RELEVANT_NEW_PROJECTS
        ),
        new ProjectStateFilterCounter(ProjectStateFilterType.LEAK_DETECTION),
        new ProjectStateFilterCounter(ProjectStateFilterType.DAMAGE_ASSESSMENT),
        new ProjectStateFilterCounter(
          ProjectStateFilterType.MISSING_DRYING_APPROVAL
        ),
        new ProjectStateFilterCounter(ProjectStateFilterType.DRYING),
        new ProjectStateFilterCounter(ProjectStateFilterType.REPAIR),
        new ProjectStateFilterCounter(
          ProjectStateFilterType.OVERDUE_RELEVANT_PROJECTS
        ),
        new ProjectStateFilterCounter(
          ProjectStateFilterType.RECENTLY_FINISHED_RELEVANT_PROJECTS
        ),
      ]
    : [];

  const myProjectsFilters = [
    new ProjectStateFilterCounter(ProjectStateFilterType.PROJECT_OWNER),
    new ProjectStateFilterCounter(ProjectStateFilterType.PROJECT_SUPERVISOR),
    new ProjectStateFilterCounter(ProjectStateFilterType.MISSING_PROJECT_OWNER),
    new ProjectStateFilterCounter(ProjectStateFilterType.FOLLOW_UP_INSPECTION),
    new ProjectStateFilterCounter(ProjectStateFilterType.OVERDUE_MY_PROJECTS),
    new ProjectStateFilterCounter(ProjectStateFilterType.RECENTLY_FINISHED),
    new ProjectStateFilterCounter(ProjectStateFilterType.MISSING_REPORT),
  ];

  const visitAllProjectPhasesFilters = context.addAllProjects;

  recentDryingProjects.forEach((projectOverview) => {
    const step = getLastestProjectState(projectOverview, today);

    if (branchFilter.doesProjectOverviewMatchFilter(projectOverview)) {
      if (visitAllProjectPhasesFilters) {
        allProjectsByPhasesFilters.forEach((filter) => {
          filter.visit(projectOverview, targetUser, userLookup, today, step);
        });
      }

      if (
        !isProjectOverviewWebsiteOnly(projectOverview)
      ) {
        appointmentManagementFilters.forEach((filter) => {
          filter.visit(projectOverview, targetUser, userLookup, today, step);
        });
      }

      relevantProjectFilters.forEach((filter) => {
        filter.visit(projectOverview, targetUser, userLookup, today, step);
      });

      myProjectsFilters.forEach((filter) => {
        filter.visit(projectOverview, targetUser, userLookup, today, step);
      });

      projectOrganisationFilters.forEach((filter) => {
        filter.visit(projectOverview, targetUser, userLookup, today, step);
      });
    }
  });

  const result = {
    appointmentManagementItems: !context.addAppointmentItems
      ? []
      : mapFilterCountersToCockpitItems(
          appointmentManagementFilters,
          CockpitCardType.APPOINTMENT_MANAGEMENT,
          true,
          context,
          ProjectSortOrder.URGENCY
        ).filter((value) => value.shouldBeDisplayed),
    projectOrganisationItems: context.createProject
      ? [
          {
            id: -1,
            data: {
              name: vue.$t("cockpit.createNewProject").toString(),
              count: "+",
            },
            infoType: CockpitEntryInfoType.Counter,
            shouldBeDisplayed: context.createProject,
            onClickCallback: () => {
              setString(STORAGE_KEY_LAST_VISITED_ITEM_LEVEL_1);

              router.push({
                path: "cockpit/createProject",
                query: {
                  newProjectScope: NewProjectScope.FLEX,
                },
              });
            },
          },
          ...mapFilterCountersToCockpitItems(
            projectOrganisationFilters,
            CockpitCardType.PROJECT_ORGANSIATION,
            true,
            context,
            ProjectSortOrder.URGENCY
          ),
        ]
      : mapFilterCountersToCockpitItems(
          projectOrganisationFilters,
          CockpitCardType.PROJECT_ORGANSIATION,
          true,
          context,
          ProjectSortOrder.URGENCY
        ),
    allProjectPhaseItems: context.addAllProjects
      ? mapFilterCountersToCockpitItems(
          allProjectsByPhasesFilters,
          CockpitCardType.ALL_PROJECTS_BY_PHASES,
          true,
          context,
          undefined,
          true
        )
      : [],
    myAppointmentItems: getMyAppointmentItems(recentProjects, context),
    myProjectItems: mapFilterCountersToCockpitItems(
      myProjectsFilters,
      CockpitCardType.MY_PROJECTS,
      true,
      context,
      ProjectSortOrder.URGENCY
    ),
    projectsNoApp: [],
    relevantProjects: mapFilterCountersToCockpitItems(
      relevantProjectFilters,
      CockpitCardType.RELEVANT_PROJECTS,
      true,
      context,
      ProjectSortOrder.NEWEST_FIRST
    ),
  };

  return result;
}
